import type { Definition, DefinitionOrBoolean } from 'typescript-json-schema';
import {
    importProperty,
    checkDeprecatedProperties,
    getManifestSectionByPathV4,
    DEFINITION_LINK_PREFIX,
    CONST_PROPERTY
} from '../../common';
import { getReflectMetadata } from '../../common/decoration/decorators';
import type { ExtensionLogger } from '../../..';
import { type Manifest } from '@sap/ux-specification-types';

type DefinitionWithControlProperty = Definition & { controlProperty: string; defaultControlProperty?: boolean };

/**
 * Transfers settings from manifest to object page config.
 *
 * @param {object} configPart - The configuration object that contains the settings to be imported.
 * @param {Manifest} manifest - The manifest object where the settings will be imported.
 * @param {Definition | DefinitionOrBoolean} appSchema - The application schema used for validation and structure.
 * @param {string} pageKey - The key representing the specific page in the manifest.
 * @param {string[]} breadcrumbs - The breadcrumb hierarchy guiding the location of the target settings.
 * @param {string} [sectionId] - The optional section ID for locating specific sections in the manifest.
 * @param {ExtensionLogger} [logger] - A logger instance used for logging deprecation warnings and issues.
 * @param {Definition | DefinitionOrBoolean} [schemaDefinitions] - Optional schema definitions that further specify the configuration structure.
 */
export function importSettingsOfObject(
    configPart: { [key: string]: any },
    manifest: Manifest,
    appSchema: Definition | DefinitionOrBoolean,
    pageKey: string,
    breadcrumbs: string[],
    sectionId?: string,
    logger?: ExtensionLogger,
    schemaDefinitions?: Definition | DefinitionOrBoolean
): void {
    if (!appSchema) {
        return;
    }
    let manifestSection = {};
    const deprecatedProperties = configPart?.getDeprecated;

    const targetAnnotationEncoded = sectionId && sectionId.replace(/\//g, '::');
    for (const propertyKey in appSchema['properties']) {
        const syncRule = getReflectMetadata(configPart, propertyKey);

        if (syncRule?.manifest) {
            const path = syncRule.manifest.path([pageKey], manifest, targetAnnotationEncoded, ...breadcrumbs);
            manifestSection = getManifestSectionByPathV4(manifest, path, sectionId, targetAnnotationEncoded, true);

            if (manifestSection && Object.keys(manifestSection).length > 0) {
                // if there are several definitions, we need to find the one that matches the manifest section element
                const property = appSchema['properties'][propertyKey];
                const propertyDefinition = getPropertyDefinition(
                    property,
                    schemaDefinitions,
                    manifestSection?.[propertyKey]
                );

                importProperty(
                    syncRule,
                    manifestSection,
                    propertyKey,
                    configPart,
                    sectionId,
                    breadcrumbs,
                    propertyDefinition as Definition
                );
                checkDeprecatedProperties(deprecatedProperties, manifestSection, path, logger);
            }
        }
    }
}

/**
 * Finds and returns the matching property definition from a set of schema definitions based on a given manifest section element.
 *
 * @param {Definition} property - Represents the property that contains potential definition variants to match against.
 * @param {Definition | DefinitionOrBoolean} schemaDefinitions - A collection of schema definitions to source the matching property definition.
 * @param {Record<string, unknown>} manifestSectionElement - An object representing the manifest section element used to determine the relevant schema definition.
 * @returns {Definition | DefinitionOrBoolean | undefined} The matching property definition, or undefined if no match is found.
 */
function getPropertyDefinition(
    property: Definition,
    schemaDefinitions?: Definition | DefinitionOrBoolean,
    manifestSectionElement?: Record<string, unknown>
): Definition | DefinitionOrBoolean | undefined {
    // if there are several definitions, we need to find the one that matches the manifest section element
    const { anyOf: definitionVariants } = property;
    if (
        !definitionVariants ||
        !schemaDefinitions ||
        !manifestSectionElement ||
        typeof manifestSectionElement !== 'object'
    ) {
        return undefined;
    }
    return definitionVariants.reduce(
        (definition: Definition | DefinitionOrBoolean | undefined, variant: Definition | DefinitionOrBoolean) => {
            if (definition) {
                return definition;
            }
            // gets the definition from the schemaDefinitions
            const definitionName = (variant as Definition).$ref?.split(DEFINITION_LINK_PREFIX)[1];
            const schemaDefinition = schemaDefinitions[definitionName];
            if (!schemaDefinition) {
                return undefined;
            }

            // use the control property to find the correct definition
            const { controlProperty, properties, defaultControlProperty } = schemaDefinition;
            const manifestValue = manifestSectionElement[controlProperty];

            // if this schema definition is the default one, we return it if the control property is not defined
            if (defaultControlProperty && manifestValue === undefined) {
                return schemaDefinition;
            }

            const controlPropertyDefinition = properties[controlProperty] ?? {};
            const definitionValue = controlPropertyDefinition[CONST_PROPERTY];
            return CONST_PROPERTY in controlPropertyDefinition && definitionValue === manifestValue
                ? schemaDefinition
                : undefined;
        },
        undefined
    );
}
